/**
 * SectionRow — a single collapsible row in the page builder section list.
 *
 * Props:
 *   section      object   — { id, type, visible, props }
 *   index        number   — position in the array
 *   total        number   — total section count (to disable move buttons at ends)
 *   isExpanded   bool     — whether the editor panel is open
 *   onToggleExpand () => void
 *   onVisibilityChange (visible: bool) => void
 *   onMoveUp     () => void
 *   onMoveDown   () => void
 *   onPropsChange (newProps: object) => void
 */

import React from 'react';
import {
  Box, Typography, Switch, IconButton, Collapse,
  Tooltip, useTheme, Chip,
} from '@mui/material';
import {
  ArrowUpward, ArrowDownward, ExpandMore, ExpandLess,
  VisibilityOff, DeleteOutline,
} from '@mui/icons-material';
import sectionFieldSchemas from './sectionFieldSchemas';
import SectionEditor from './SectionEditor';
import { glass, ADMIN_GRADIENTS } from '../ui/glassTokens';

export default function SectionRow({
  section,
  index,
  total,
  isExpanded,
  onToggleExpand,
  onVisibilityChange,
  onMoveUp,
  onMoveDown,
  onPropsChange,
  onRemove,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const schema = sectionFieldSchemas[section.type] || { label: section.type, fields: [] };
  const hasFields = schema.fields && schema.fields.length > 0;

  return (
    <Box
      sx={{
        ...g.surface,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
        opacity: section.visible ? 1 : 0.65,
        '&:hover': {
          boxShadow: isDark
            ? '0 8px 32px rgba(99,102,241,0.12), 0 2px 8px rgba(2,6,23,0.45)'
            : '0 8px 32px rgba(99,102,241,0.10), 0 2px 8px rgba(15,23,42,0.10)',
        },
      }}
    >
      {/* ── Collapsed header row ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: hasFields ? 'pointer' : 'default',
          userSelect: 'none',
        }}
        onClick={hasFields ? onToggleExpand : undefined}
        role={hasFields ? 'button' : undefined}
        aria-expanded={hasFields ? isExpanded : undefined}
      >
        {/* Index badge */}
        <Box
          sx={{
            minWidth: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ color: '#6366F1' }}>
            {index + 1}
          </Typography>
        </Box>

        {/* Section label */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              color: isDark ? '#F8FAFC' : '#0F172A',
              lineHeight: 1.2,
            }}
            noWrap
          >
            {schema.label}
          </Typography>
          {!section.visible && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <VisibilityOff sx={{ fontSize: 11, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled">
                Hidden
              </Typography>
            </Box>
          )}
        </Box>

        {/* Controls — stop propagation so clicks don't toggle expand */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Visibility toggle */}
          <Tooltip title={section.visible ? 'Hide section' : 'Show section'}>
            <Switch
              size="small"
              checked={!!section.visible}
              onChange={e => onVisibilityChange(e.target.checked)}
              inputProps={{ 'aria-label': `Toggle visibility of ${schema.label}` }}
            />
          </Tooltip>

          {/* Move up */}
          <Tooltip title="Move up">
            <span>
              <IconButton
                size="small"
                onClick={onMoveUp}
                disabled={index === 0}
                aria-label="Move section up"
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Move down */}
          <Tooltip title="Move down">
            <span>
              <IconButton
                size="small"
                onClick={onMoveDown}
                disabled={index === total - 1}
                aria-label="Move section down"
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Expand toggle */}
          {hasFields && (
            <Tooltip title={isExpanded ? 'Collapse' : 'Edit fields'}>
              <IconButton
                size="small"
                onClick={onToggleExpand}
                aria-label={isExpanded ? 'Collapse editor' : 'Expand editor'}
              >
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}

          {/* Remove section */}
          {onRemove && (
            <Tooltip title="Remove section">
              <IconButton
                size="small"
                color="error"
                onClick={onRemove}
                aria-label={`Remove ${schema.label}`}
                sx={{ ml: 0.5 }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Expanded editor panel — lazy mounted ── */}
      {hasFields && (
        <Collapse in={isExpanded} unmountOnExit>
          <Box
            sx={{
              borderTop: `1px solid ${g.border}`,
              p: 2.5,
              background: isDark
                ? 'rgba(0,0,0,0.18)'
                : 'rgba(246,247,251,0.6)',
            }}
          >
            <SectionEditor
              schema={schema}
              props={section.props || {}}
              onChange={onPropsChange}
            />
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
